import axios from "axios";
import { StorageKey } from "../utils/constants.ts";

const storageType = window.sessionStorage;

export type IConfig = {
  env: "prod" | "production" | "demo" | "test" | "staging" | "unstable" | "development" | "dev",
  apiUrl: string,
  csrfUrl: string,
  app: {
    "gitRev": string,
    "name": string,
    "nodeVersion": string,
    "buildTime": string,
    "version": string[]
  }
};


type OnLoadCallback = (config: IConfig) => void;

/**
 * @class ConfigService
 * A singleton wrapper for acquiring and storing configuration data
 * in session storage
 */
export default class ConfigService {
  private static _instance: ConfigService;
  private _config: IConfig = {
    env: "dev",
    app: {
      "gitRev": "dev",
      "name": "dev",
      "nodeVersion": "dev",
      "buildTime": "dev",
      "version": ["dev"]
    },
    apiUrl: "",
    csrfUrl: "",
  };
  private _onLoadSet = new Set<OnLoadCallback>();

  /**
   * @constructor
   */
  private constructor() {
  }

  public get config(): IConfig {
    return this._config;
  }

  private set config(config: IConfig) {
    this._config = config;
    this._onLoadSet.forEach((cb: OnLoadCallback) => {
      cb(config);
    });
  }

  public static get instance(): ConfigService {
    if (!ConfigService._instance) {
      ConfigService._instance = new ConfigService();
    }

    return ConfigService._instance;
  }

  /**
   * @function init
   * Initializes the ConfigService singleton
   * @returns {Promise<ConfigService>} An instance of ConfigService
   */
  public static async init(): Promise<ConfigService> {
    return new Promise(async (resolve) => {

      /*const _cachedConfig = this.loadCache();
      if (_cachedConfig !== null) {
        ConfigService.instance.config = _cachedConfig;
      }
      */
      await ConfigService.instance.reload();

      resolve(ConfigService.instance);
    });
  }

  public async reload(): Promise<void> {
    const _loadedConfig = await ConfigService.requestConfig();

    if (!_loadedConfig) {
      return;
    }

    storageType.setItem(StorageKey.CONFIG, JSON.stringify(_loadedConfig));
    this.config = _loadedConfig;
  }

  public static loadCache(): IConfig | null {
    try {
      const _cachedConfig = storageType.getItem(StorageKey.CONFIG);
      if (_cachedConfig !== null) {
        return JSON.parse(_cachedConfig as string) as IConfig;
      }

      return null;
    } catch (err: unknown) {
      console.error(`Unparseable configuration: ${err}`);
      return null;
    }
  }

  public static async requestConfig(): Promise<IConfig | null> {
    console.warn("Loading configuration...");
    const config = await axios
      .get("/config.json", {
        baseURL: "",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache"
        }
      })
      .then(({ data }) => {
        return data;
      })
      .catch((err) => {
        console.error(`Failed to reacquire configuration: ${err}`);
      });

    if (!config) {
      return null;
    }
    console.warn("Loaded", config);

    return config;
  }

  isProduction(): boolean {
    return this.config.env === "production"
      || this.config.env === "prod";
  }

  isStaging(): boolean {
    return this.config.env === "staging";
  }

  isDevelopment(): boolean {
    return this.config.env === "development"
      || this.config.env === "dev";
  }

  isTesting(): boolean {
    return this.config.env === "test";
  }

  isDemo(): boolean {
    return this.config.env === "demo";
  }

  getWebSocketDomain() {
    return document.location.origin.replace(/^http/, "ws");
  }

  public onLoad(fn: OnLoadCallback): void {
    this._onLoadSet.add(fn);
  }
}
